import { useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';

import { formatRelativeOrAbsolute, toMachineDate } from '@shared/format';
import {
  DTable,
  DTableBody,
  DTableHead,
  DTableRowHeader,
  DTableScroll,
  RowSkeleton,
} from '@shared/ui/dtable';
import { EmptyState } from '@shared/ui/EmptyState';
import { StateBadge } from '@shared/ui/StateBadge';

import { caseStateBadgeKind } from '../_shared/case-states';
import { DetailSection, SurfaceError } from '../_shared/components';
import type { UserCaseRow, UserId } from '../_shared/types';
import { copy } from './copy';
import { errors } from './errors';
import { userCasesKey, useUserCases } from './queries';

const TABLE_COLUMNS = 5;

/**
 * §5 سجلّ الحالات — the queue's own records, read here rather than re-invented.
 *
 * **Every row opens the case it names.** A history table whose links resolve to
 * a different record than the row describes is the defect this section is
 * written against: the identifier, the subject, the state, and the date all come
 * from `backend-support`'s admin row, and the link carries that row's own id.
 *
 * **The identifier is a column, because it is how a case is named.** A case has
 * no title — a reason from a configured list plus free text and nothing else
 * nameable — so `CS-4192` is what either party quotes over the phone. It renders
 * ASCII in the mono face inside `<bdi>` and is never re-spelled in Arabic-Indic
 * digits: an identifier transcribed into another digit set is a wrong
 * identifier.
 *
 * **The state badge maps a configured value, and degrades rather than
 * disappearing.** `CASE_STATES` is a setting; a value this console has no badge
 * for renders neutral with its literal token, because the badge taxonomy is
 * closed while the vocabulary is not.
 *
 * **No traversal control and no truncation line.** The section links out to the
 * queue, which is where the full record is walked; the strip's endpoint pages by
 * cursor and reports no total, so a «shown of» line here would have no honest
 * second figure to state.
 */
export function CaseHistorySection({ userId }: { userId: UserId }) {
  const query = useUserCases(userId);
  const queryClient = useQueryClient();

  const rows = query.data?.items ?? [];

  return (
    <DetailSection
      id="user-cases"
      heading={copy.cases.heading}
      context={copy.cases.context}
      action={
        <Link
          to="/cases"
          className="hit-target flex items-center rounded-sm px-3 text-sm font-semibold text-ink no-underline hover:bg-hover-bg"
        >
          {copy.cases.queueLink}
        </Link>
      }
    >
      {query.isError ? (
        <SurfaceError
          error={query.error}
          catalog={errors}
          onRetry={() => void queryClient.invalidateQueries({ queryKey: userCasesKey(userId) })}
        />
      ) : null}

      {query.isPending ? (
        <>
          <p role="status" className="sr-only">
            {copy.states.loading}
          </p>
          <CaseTable>
            <RowSkeleton columns={TABLE_COLUMNS} rows={3} />
          </CaseTable>
        </>
      ) : null}

      {query.data ? (
        rows.length === 0 ? (
          <EmptyState message={copy.cases.empty} />
        ) : (
          <CaseTable>
            {rows.map((row) => (
              <CaseRow key={row.id} row={row} />
            ))}
          </CaseTable>
        )
      ) : null}
    </DetailSection>
  );
}

function CaseRow({ row }: { row: UserCaseRow }) {
  const type = copy.cases.types[row.caseType] ?? row.caseType;
  const state = copy.cases.states[row.caseState] ?? row.caseState;

  return (
    <tr>
      <td>
        <StateBadge kind="neutral">{type}</StateBadge>
      </td>

      {/* The cell that names the row, and the one that carries its link. */}
      <DTableRowHeader>
        <Link to={`/cases/${row.id}`} className="text-ink hover:underline">
          {row.subjectLabel ?? copy.cases.noSubject}
        </Link>
      </DTableRowHeader>

      <td className="mono whitespace-nowrap">
        <bdi>{row.caseReference}</bdi>
      </td>

      <td>
        <StateBadge kind={caseStateBadgeKind(row.caseState)}>{state}</StateBadge>
      </td>

      <td className="whitespace-nowrap">
        {/*
          Relative inside a week and absolute after it, with the ASCII ISO-8601
          machine value on the same element. Every sort over a date anywhere
          reads that attribute, never the rendered «قبل يومين».
        */}
        <time dateTime={toMachineDate(row.lastActivityAt)}>
          {formatRelativeOrAbsolute(row.lastActivityAt)}
        </time>
      </td>
    </tr>
  );
}

function CaseTable({ children }: { children: React.ReactNode }) {
  return (
    <DTableScroll>
      <DTable caption={copy.cases.caption} minInlineSize="42rem">
        <DTableHead>
          <tr>
            <th scope="col">{copy.cases.columns.type}</th>
            <th scope="col">{copy.cases.columns.subject}</th>
            <th scope="col">{copy.cases.columns.reference}</th>
            <th scope="col">{copy.cases.columns.state}</th>
            <th scope="col">{copy.cases.columns.updated}</th>
          </tr>
        </DTableHead>
        <DTableBody>{children}</DTableBody>
      </DTable>
    </DTableScroll>
  );
}
